// contexts/ResetContext.js - Updated with proper context registration
import React, { createContext, useContext, useRef, useCallback } from 'react';

// Create the context
const ResetContext = createContext();

// Custom hook to use reset functionality
export const useReset = () => {
  const context = useContext(ResetContext);
  if (!context) {
    throw new Error('useReset must be used within a ResetProvider');
  }
  return context;
};

export const ResetProvider = ({ children }) => { 
  const resetFunctions = useRef(new Map()); // Changed from Set to Map
  const resetStateFunctions = useRef(new Map());
  const isResettingRef = useRef(false);

  // ✅ Register reset with context name and type
  const registerReset = useCallback((contextName, resetFn, resetState = null, contextType = 'default') => {
    if (resetFn) {
      // Store with metadata
      resetFunctions.current.set(contextName, { 
        resetFn, 
        contextType,
        contextName 
      });
      
      if (resetState) {
        resetStateFunctions.current.set(contextName, { resetFn, resetState, contextType });
      }
      
      console.log(`📝 Registered reset for: ${contextName} (type: ${contextType})`);
    }
  }, []);

  const unregisterReset = useCallback((contextName) => {
    if (contextName) {
      resetFunctions.current.delete(contextName);
      resetStateFunctions.current.delete(contextName);
      console.log(`🗑️ Unregistered reset for: ${contextName}`);
    }
  }, []);

  // ✅ Improved selective reset with proper context filtering
  const resetAll = useCallback(async (options = {}) => {
    const { 
      skipAuth = true,      // Skip authentication contexts
      skipProfile = true,   // Skip profile contexts
      skipMatch = false,
      skipDiscover = false,
      skipChat = false,
    } = options;
    
    // Prevent multiple resets at once
    if (isResettingRef.current) {
      console.log('⚠️ Reset already in progress, skipping');
      return;
    }
    
    isResettingRef.current = true;
    console.log('🔄 Resetting application state...');
    console.log('📋 Options:', { skipAuth, skipProfile, skipMatch, skipDiscover, skipChat });
    
    const promises = [];
    const skippedContexts = [];
    const resetContexts = [];
    
    // ✅ Iterate over the Map entries
    for (const [contextName, contextData] of resetFunctions.current) {
      const { resetFn, contextType } = contextData;
      
      // ✅ Determine if this context should be skipped
      let shouldSkip = false;
      let skipReason = '';
      
      // Check based on context type
      if (contextType === 'auth' || contextType === 'Auth') {
        if (skipAuth) {
          shouldSkip = true;
          skipReason = 'Auth (skipAuth)';
        }
      } else if (contextType === 'profile' || contextType === 'Profile') {
        if (skipProfile) {
          shouldSkip = true;
          skipReason = 'Profile (skipProfile)';
        }
      } else if (contextType === 'match' || contextName.toLowerCase().includes('match')) {
        if (skipMatch) {
          shouldSkip = true;
          skipReason = 'Match (skipMatch)';
        }
      } else if (contextType === 'discover' || contextName.toLowerCase().includes('discover')) {
        if (skipDiscover) {
          shouldSkip = true;
          skipReason = 'Discover (skipDiscover)';
        }
      } else if (contextType === 'chat' || contextName.toLowerCase().includes('chat')) {
        if (skipChat) {
          shouldSkip = true;
          skipReason = 'Chat (skipChat)';
        }
      }
      
      // ✅ Also check context name for common patterns
      if (!shouldSkip) {
        const nameLower = contextName.toLowerCase();
        if (skipAuth && (nameLower.includes('auth') || nameLower.includes('login') || nameLower.includes('session'))) {
          shouldSkip = true;
          skipReason = 'Auth (name match)';
        } else if (skipProfile && (nameLower.includes('profile') || nameLower.includes('user'))) {
          shouldSkip = true;
          skipReason = 'Profile (name match)';
        }
      }
      
      if (shouldSkip) {
        skippedContexts.push(`${contextName} (${skipReason})`);
        console.log(`⏭️ Skipping reset for: ${contextName} - ${skipReason}`);
      } else {
        resetContexts.push(contextName);
        try {
          const result = resetFn();
          if (result instanceof Promise) {
            promises.push(result);
          }
        } catch (error) {
          console.error(`❌ Error resetting ${contextName}:`, error);
        }
      }
    }
    
    console.log(`🔄 Resetting contexts: ${resetContexts.join(', ') || 'none'}`);
    
    try {
      await Promise.all(promises);
      console.log('✅ State reset successfully');
      
      if (skippedContexts.length > 0) {
        console.log(`⏭️ Skipped contexts:\n${skippedContexts.map(s => `  - ${s}`).join('\n')}`);
      }
      
      if (resetContexts.length === 0) {
        console.log('⚠️ No contexts were reset (all skipped)');
      }
      
    } catch (error) {
      console.error('❌ Error resetting state:', error);
    } finally {
      isResettingRef.current = false;
    }
  }, []);

  // Reset only specific contexts
  const resetSpecific = useCallback(async (contextNames) => {
    if (!contextNames || contextNames.length === 0) {
      console.log('⚠️ No contexts specified for reset');
      return;
    }
    
    console.log(`🔄 Resetting specific contexts: ${contextNames.join(', ')}`);
    
    const promises = [];
    
    for (const [name, entry] of resetStateFunctions.current) {
      if (contextNames.includes(name)) {
        try {
          const result = entry.resetFn();
          if (result instanceof Promise) {
            promises.push(result);
          }
        } catch (error) {
          console.error(`Error resetting ${name}:`, error);
        }
      }
    }
    
    try {
      await Promise.all(promises);
      console.log(`✅ Reset complete for: ${contextNames.join(', ')}`);
    } catch (error) {
      console.error('❌ Error resetting specific contexts:', error);
    }
  }, []);

  // Clear all reset functions
  const clearAllResets = useCallback(() => {
    resetFunctions.current.clear();
    resetStateFunctions.current.clear();
    console.log('🗑️ All reset functions cleared');
  }, []);

  const value = {
    resetAll,
    resetSpecific,
    registerReset,
    unregisterReset,
    clearAllResets,
    isResetting: isResettingRef.current,
  };

  return (
    <ResetContext.Provider value={value}>
      {children}
    </ResetContext.Provider>
  );
};